import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { EStoreServiceType } from "../../Enum/EStoreServiceType"
import { calculateDistanceInMeters } from "../../utils/geo"

export const DEFAULT_LOCAL_SERVICE_RADIUS_METERS = 100

const hasValidCoordinates = (latitude?: number, longitude?: number): latitude is number =>
  typeof latitude === "number" &&
  Number.isFinite(latitude) &&
  typeof longitude === "number" &&
  Number.isFinite(longitude)

const resolvePosition = async (): Promise<{ latitude: number; longitude: number } | null> => {
  if (!navigator.geolocation) {
    return null
  }

  return await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => resolve(null),
      {
        maximumAge: 300000,
        timeout: 8000,
        enableHighAccuracy: false,
      },
    )
  })
}

interface ResolveOrderModeByProximityParams {
  storeServiceType: EStoreServiceType
  storeLatitude?: number
  storeLongitude?: number
  localServiceRadiusMeters?: number
}

export class OrderModeProximityHelper {
  static async resolveInitialOrderMode(params: ResolveOrderModeByProximityParams): Promise<EOrderPlaceType> {
    if (params.storeServiceType === EStoreServiceType.Local) {
      return EOrderPlaceType.Local
    }

    if (params.storeServiceType === EStoreServiceType.Delivery) {
      return EOrderPlaceType.Delivery
    }

    if (!hasValidCoordinates(params.storeLatitude, params.storeLongitude)) {
      return EOrderPlaceType.Delivery
    }

    const currentPosition = await resolvePosition()

    if (!currentPosition) {
      return EOrderPlaceType.Delivery
    }

    const radiusInMeters =
      typeof params.localServiceRadiusMeters === "number" && params.localServiceRadiusMeters > 0
        ? params.localServiceRadiusMeters
        : DEFAULT_LOCAL_SERVICE_RADIUS_METERS

    const distanceInMeters = calculateDistanceInMeters(
      currentPosition.latitude,
      currentPosition.longitude,
      params.storeLatitude,
      params.storeLongitude,
    )

    return distanceInMeters <= radiusInMeters ? EOrderPlaceType.Local : EOrderPlaceType.Delivery
  }
}
